import type { IdeaTask, TaskStatus, Priority } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { 
  AlertCircle,
  CheckCircle2,
  Circle,
  Clock,
  FlaskConical,
  Hammer,
  Search,
  Users,
  FileText,
  Sparkles,
  Pencil,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface PlanGridProps {
  tasks: IdeaTask[];
  onEditTask?: (task: IdeaTask) => void;
}

const STATUS_CONFIG: Record<TaskStatus, { label: string; icon: typeof Circle; className: string }> = {
  next: { label: 'Next', icon: Circle, className: 'text-muted-foreground' },
  doing: { label: 'Doing', icon: Clock, className: 'text-primary' },
  blocked: { label: 'Blocked', icon: AlertCircle, className: 'text-destructive' },
  done: { label: 'Done', icon: CheckCircle2, className: 'text-primary' },
};

const TYPE_ICONS: Record<string, typeof FlaskConical> = {
  experiment: FlaskConical,
  build: Hammer,
  research: Search,
  outreach: Users,
  admin: FileText,
};

const PRIORITY_STYLES: Record<Priority, string> = {
  low: 'bg-muted/50 text-muted-foreground',
  medium: 'bg-secondary/30 text-secondary-foreground',
  high: 'bg-primary/20 text-primary',
};

export function PlanGrid({ tasks, onEditTask }: PlanGridProps) {
  if (tasks.length === 0) {
    return (
      <p className="text-sm text-muted-foreground/60 text-center py-8 italic">
        No tasks yet.
      </p>
    );
  }

  // Sort: doing first, then by position
  const sortedTasks = [...tasks].sort((a, b) => {
    const statusOrder: Record<TaskStatus, number> = { doing: 0, next: 1, blocked: 2, done: 3 };
    const statusDiff = statusOrder[a.status] - statusOrder[b.status];
    if (statusDiff !== 0) return statusDiff;
    return a.position - b.position;
  });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
      {sortedTasks.map((task) => {
        const StatusIcon = STATUS_CONFIG[task.status].icon;
        const TypeIcon = TYPE_ICONS[task.task_type] || FileText;

        return (
          <div 
            key={task.id}
            className={cn(
              "group flex items-center gap-2 p-2 bg-card border rounded-md text-xs transition-colors",
              task.status === 'done' && "opacity-50",
              task.status === 'blocked' && "border-destructive/30",
              onEditTask && "cursor-pointer hover:border-primary/30 hover:bg-muted/30"
            )}
            onClick={() => onEditTask?.(task)}
          >
            <StatusIcon className={cn("h-3.5 w-3.5 shrink-0", STATUS_CONFIG[task.status].className)} />
            
            <span className={cn(
              "flex-1 truncate font-medium",
              task.status === 'done' && "line-through text-muted-foreground"
            )}>
              {task.title}
            </span>

            {task.source === 'ai' && (
              <Sparkles className="h-3 w-3 text-primary/40 shrink-0" />
            )}

            {task.due_date && (
              <span className="text-muted-foreground shrink-0">
                {format(new Date(task.due_date), 'M/d')}
              </span>
            )}

            <Badge className={cn("h-4 text-[9px] shrink-0", PRIORITY_STYLES[task.priority])}>
              {task.priority[0].toUpperCase()}
            </Badge>

            <TypeIcon className="h-3 w-3 text-muted-foreground shrink-0" />

            {onEditTask && (
              <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            )}
          </div>
        );
      })}
    </div>
  );
}